/**
 * ProductSyncJob - TypeScript model for product sync job data
 */

export type JobType = 'full_sync' | 'incremental_sync' | 'clear_deleted';
export type JobStatus = 'pending' | 'in_progress' | 'syncing' | 'completed' | 'failed' | 'cancelled';

export interface ProductSyncJobMetadata {
  currentStep?: string;
  progressPercent?: number;
  s3FilesDeleted?: number;
  etlRecordsCleared?: number;
  pendingPeriods?: number;
  totalProductsSynced?: number;
}

export interface ProductSyncJob {
  id: number;
  merchantId: number;
  jobType: JobType;
  status: JobStatus;
  initiatedBy: string;
  initiatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  stepsCompleted: string[];
  metadata: ProductSyncJobMetadata;
  totalProductsSynced: number;
  deletedProductsCleared: number;
  progressPercent?: number;
  duration?: number; // In seconds, calculated from history
}

export interface InitiateClearDeletedResponse {
  message: string;
  data: {
    jobId: number;
    status: JobStatus;
    estimatedDuration: string;
    initiatedAt: string;
  };
}

export interface GetSyncStatusResponse {
  data: ProductSyncJob;
}

export interface GetActiveJobResponse {
  data: ProductSyncJob | null;
  message?: string;
}

export interface GetSyncHistoryResponse {
  data: ProductSyncJob[];
  count: number;
}

export interface CancelSyncResponse {
  message: string;
  data: {
    id: number;
    status: JobStatus;
  };
}

/**
 * ProductSyncJobModel - Class for ProductSyncJob data manipulation
 */
export class ProductSyncJobModel implements ProductSyncJob {
  public id: number = 0;
  public merchantId: number = 0;
  public jobType: JobType = 'clear_deleted';
  public status: JobStatus = 'pending';
  public initiatedBy: string = '';
  public initiatedAt: string = '';
  public startedAt: string | null = null;
  public completedAt: string | null = null;
  public errorMessage: string | null = null;
  public stepsCompleted: string[] = [];
  public metadata: ProductSyncJobMetadata = {};
  public totalProductsSynced: number = 0;
  public deletedProductsCleared: number = 0;
  public progressPercent: number = 0;
  public duration?: number;

  constructor(data?: Partial<ProductSyncJob>) {
    if (data) {
      this.mapData(data);
    }
  }

  public mapData(data: Partial<ProductSyncJob>) {
    this.id = data.id ?? this.id;
    this.merchantId = data.merchantId ?? this.merchantId;
    this.jobType = data.jobType ?? this.jobType;
    this.status = data.status ?? this.status;
    this.initiatedBy = data.initiatedBy ?? this.initiatedBy;
    this.initiatedAt = data.initiatedAt ?? this.initiatedAt;
    this.startedAt = data.startedAt ?? this.startedAt;
    this.completedAt = data.completedAt ?? this.completedAt;
    this.errorMessage = data.errorMessage ?? this.errorMessage;
    this.stepsCompleted = data.stepsCompleted ?? this.stepsCompleted;
    // Merge metadata to preserve existing keys when backend sends partial updates
    this.metadata = { ...this.metadata, ...data.metadata };
    this.totalProductsSynced = data.totalProductsSynced ?? this.totalProductsSynced;
    this.deletedProductsCleared = data.deletedProductsCleared ?? this.deletedProductsCleared;
    this.progressPercent = data.progressPercent ?? this.metadata?.progressPercent ?? 0;
    this.duration = data.duration;
  }

  /**
   * Check if job is active (pending, in_progress, or syncing)
   */
  public isActive(): boolean {
    return this.status === 'pending' || this.status === 'in_progress' || this.status === 'syncing';
  }

  /**
   * Check if job can be cancelled
   */
  public canCancel(): boolean {
    if (!this.isActive()) return false;
    return !this.stepsCompleted.includes('TRIGGER_SYNC');
  }

  /**
   * Get human-readable status label
   */
  public getStatusLabel(): string {
    const labels: Record<JobStatus, string> = {
      pending: 'Pending',
      in_progress: 'In Progress',
      syncing: 'Syncing',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return labels[this.status] || this.status;
  }

  /**
   * Get status color for UI
   */
  public getStatusColor(): string {
    const colors: Record<JobStatus, string> = {
      pending: 'warning',
      in_progress: 'primary',
      syncing: 'info',
      completed: 'success',
      failed: 'error',
      cancelled: 'secondary'
    };
    return colors[this.status] || 'secondary';
  }

  /**
   * Get current step label
   */
  public getCurrentStep(): string {
    return this.metadata?.currentStep || 'Initializing...';
  }

  /**
   * Get formatted duration string
   */
  public getFormattedDuration(): string {
    if (this.duration == null) return '-';

    const minutes = Math.floor(this.duration / 60);
    const seconds = this.duration % 60;

    if (minutes === 0) {
      return `${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  }

  /**
   * Get formatted date string
   */
  public getFormattedDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  }
}
